import { Capacitor } from '@capacitor/core';

// Os DOIS endereços públicos do Orçamigo. Servem o mesmo bundle, com papéis
// diferentes — e a distinção é decisão de produto, não detalhe de infra:
//
//  - PUBLIC_SITE_URL é o endereço OFICIAL, e é ele que vai em todo link que sai
//    para alguém de fora: orçamento e contrato para o cliente do prestador,
//    convite de equipe, indicação. O cliente recebe `orcamigo.com.br/o/…`.
//  - APP_URL é o endereço da aplicação. Serve de destino para os redirects de
//    autenticação disparados pelo app NATIVO, onde `window.location.origin` é
//    `localhost` e produziria um link morto no e-mail (#15).
//
// `orca-pro2.lovable.app` continua no ar de propósito: todo orçamento e contrato
// já enviado a um cliente aponta para lá, e esses links não podem morrer.
export const PUBLIC_SITE_URL = 'https://orcamigo.com.br';

/**
 * Endereço da aplicação, usado como destino dos redirects de autenticação
 * disparados pelo app NATIVO.
 *
 * ⚠️ Ele aponta para o mesmo host do site, e isso é DELIBERADO: a hospedagem
 * serve um único domínio primário e responde **302** em todos os outros —
 * medido, `https://app.orcamigo.com.br/auth/callback` redireciona para
 * `https://orcamigo.com.br/auth/callback`. O salto extra cai justamente no
 * caminho que carrega `access_token`/`refresh_token` no FRAGMENTO da URL, que
 * o navegador só reanexa ao destino por convenção — e uma sessão perdida ali
 * aparece como "o link do e-mail não me deixa entrar", sem erro nenhum (#15).
 *
 * A constante continua existindo separada porque o PAPEL é outro (endereço da
 * aplicação × endereço público dos documentos). No dia em que `app.` passar a
 * ser servido de verdade, muda-se esta linha e mais nada.
 */
export const APP_URL = PUBLIC_SITE_URL;

/** Hosts em que o link gerado deve continuar sendo o do próprio host. */
function isDevelopmentHost(hostname: string): boolean {
  if (hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '[::1]') return true;
  // Preview/staging do Lovable: gerar link de produção a partir dali mandaria o
  // testador para o app de verdade, sem aviso.
  return hostname.endsWith('.lovable.app');
}

/**
 * Base para links públicos (`/o/:id`, `/c/:token`).
 *
 * No app instalado a origem é `localhost` — por isso o teste é
 * `Capacitor.isNativePlatform()`, e não a string da origem. Em produção web o
 * canônico é forçado: o prestador pode estar usando o app por
 * `app.orcamigo.com.br`, mas o link que ele manda ao cliente é sempre
 * `orcamigo.com.br`.
 */
export function getPublicBaseUrl(): string {
  if (Capacitor.isNativePlatform()) return PUBLIC_SITE_URL;
  if (typeof window === 'undefined') return PUBLIC_SITE_URL;
  if (isDevelopmentHost(window.location.hostname)) return window.location.origin;
  return PUBLIC_SITE_URL;
}

/**
 * Generate a public quote URL
 */
export function getPublicQuoteUrl(quoteId: string): string {
  return `${getPublicBaseUrl()}/o/${quoteId}`;
}

/**
 * Generate a public contract signing URL. Uses the non-enumerable public_token
 * (NOT the contract id) so the link cannot be guessed.
 */
export function getPublicContractUrl(publicToken: string): string {
  return `${getPublicBaseUrl()}/c/${publicToken}`;
}

/** sessionStorage key for a post-authentication redirect (survives OAuth). */
export const POST_AUTH_REDIRECT_KEY = 'orcamigo_post_auth_redirect';

/**
 * Validate a `next`/redirect path so it can only ever point back into this app
 * (a same-origin, absolute path). Rejects protocol-relative (`//host`),
 * absolute URLs, and backslash tricks. Returns `fallback` when unsafe.
 */
export function sanitizeInternalPath(raw: string | null | undefined, fallback = '/'): string {
  if (!raw) return fallback;
  const value = raw.trim();
  // Must be a root-relative path, and not protocol-relative (`//`, `/\`).
  if (!value.startsWith('/')) return fallback;
  if (value.startsWith('//') || value.startsWith('/\\')) return fallback;
  return value;
}
